import {
  CARE_NAVIGATION_CRITERIA,
  type CareNavigationCriteria,
  type CareNavigationDefinitionResponse,
  type CareNavigationMonthlyCompletionResponse,
} from "./careNavigationCriteria";
import {
  summarizeCompletionRows,
  summarizeNavigatorAttrition,
} from "./careNavigationDefinitionState";

type Counts = ReturnType<typeof summarizeCompletionRows>["current"];

export function buildCareNavigationMetricsCsvRows(props: {
  monthStart: string;
  dateEnd: string;
  rows: CareNavigationDefinitionResponse["rows"];
  billingRows: CareNavigationMonthlyCompletionResponse["rows"];
  durations: Array<[string, Counts]>;
  navigators: string[];
  criteria: CareNavigationCriteria;
}) {
  const selected = (name: string) => !props.navigators.length || props.navigators.includes(name);
  const rows = props.rows.filter(
    (row) => row.monthStart === props.monthStart && selected(row.navigatorName),
  );
  const billingRows = props.billingRows.filter(
    (row) => row.monthStart === props.monthStart && selected(row.navigatorName),
  );
  const summary = summarizeCompletionRows(rows, props.monthStart);
  const attrition = summarizeNavigatorAttrition(rows, props.monthStart);
  const countsByNavigator = new Map(summary.navigators);
  const durations = new Map(props.durations.filter(([name]) => selected(name)));
  const names = new Set([
    ...countsByNavigator.keys(),
    ...billingRows.map((row) => row.navigatorName),
    ...durations.keys(),
  ]);
  const ratio = (count: number | null | undefined, total: number | undefined) =>
    count != null && total ? count / total : null;
  const safeCell = (value: string) => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);

  return [...names].sort().map((name) => {
    const counts = countsByNavigator.get(name);
    // These rows are already account-scoped. A shared label can still represent
    // multiple selected accounts, so its display aggregate must include each one.
    const namedBilling = billingRows.filter((row) => row.navigatorName === name);
    const billing = namedBilling.length
      ? {
          alignedCount: namedBilling.reduce((sum, row) => sum + row.alignedCount, 0),
          completedCount: namedBilling.reduce((sum, row) => sum + row.completedCount, 0),
          bookedRemainingCount: namedBilling.every((row) => row.bookedRemainingCount !== undefined)
            ? namedBilling.reduce((sum, row) => sum + (row.bookedRemainingCount ?? 0), 0)
            : undefined,
        }
      : undefined;
    const duration = durations.get(name);
    const assigned = attrition.get(name);
    const navigatorRows = rows.filter((row) => row.navigatorName === name);
    return {
      Month: props.monthStart.slice(0, 7),
      "Through date": props.dateEnd,
      "Care navigator": safeCell(name),
      "Aligned CareSpaces": counts?.aligned ?? billing?.alignedCount ?? 0,
      "DCMP billed": billing?.completedCount ?? null,
      "Billed completion": ratio(billing?.completedCount, billing?.alignedCount),
      "Booked remaining": billing?.bookedRemainingCount ?? null,
      "Projected billed completion":
        billing?.bookedRemainingCount === undefined
          ? null
          : ratio(billing.completedCount + billing.bookedRemainingCount, billing.alignedCount),
      "Qualifying calls completed": counts?.completed ?? null,
      "Qualifying call completion": ratio(counts?.completed, counts?.aligned),
      "Qualifying booked remaining": counts?.bookedRemaining ?? null,
      "Email completions": counts?.emailCompleted ?? null,
      "Email and calls completed": counts?.combinedCompleted ?? null,
      "Email finalized":
        navigatorRows.length > 0 && navigatorRows.every((row) => row.emailFinalized === true),
      "Monthly calls": duration?.callDurationCount ?? null,
      "Monthly call minutes": duration?.callDurationMinutes ?? null,
      "Average monthly call minutes": ratio(
        duration?.callDurationMinutes,
        duration?.callDurationCount,
      ),
      "Needs assessment calls": counts?.needsAssessmentDurationCount ?? null,
      "Average needs assessment minutes": ratio(
        counts?.needsAssessmentDurationMinutes,
        counts?.needsAssessmentDurationCount,
      ),
      "Care plans updated": counts?.carePlanUpdated ?? null,
      "Care plan eligible": counts?.carePlanEligible ?? null,
      "Care plan completion": ratio(counts?.carePlanUpdated, counts?.carePlanEligible),
      "Reassessments completed": counts?.reassessmentCount ?? null,
      "Reassessments due": counts?.reassessmentDenominator ?? null,
      "Reassessments in window": navigatorRows.reduce(
        (sum, row) => sum + row.reassessmentInWindowCount,
        0,
      ),
      "Reassessments upcoming": navigatorRows.reduce(
        (sum, row) => sum + row.reassessmentUpcomingCount,
        0,
      ),
      "Reassessments overdue": navigatorRows.reduce(
        (sum, row) => sum + row.reassessmentOverdueCount,
        0,
      ),
      "Reassessments pending": navigatorRows.reduce(
        (sum, row) => sum + row.reassessmentPendingCount,
        0,
      ),
      "Assigned CareSpaces": assigned?.assigned ?? null,
      "Offboarded CareSpaces": assigned?.attrited ?? null,
      Attrition: ratio(assigned?.attrited, assigned?.assigned),
      ...Object.fromEntries(
        CARE_NAVIGATION_CRITERIA.map(({ id, label }) => [
          `Criterion: ${label}`,
          props.criteria[id],
        ]),
      ),
    };
  });
}
